import { useCallback, useEffect, useMemo, useRef } from 'react';

import { savableViewState } from '../utils/relatedListSwitcher';
import { resetRelatedListState, saveRelatedListState } from '../services/relatedListState';

/** How long a run of changes (chips removed one after another) waits before it is saved once. */
export const SAVE_DELAY_MS = 600;

const VIEWS = ['grid', 'table'];

/**
 * What the account keeps of a list's view state: `{ filter, view }`. Taken
 * through `savableViewState` (KPR-740's one door for this), so the search
 * box's term — a top-level `text ~ "…"` in the list's query text — is never
 * part of it (ruled t03); a text condition the builder nested under or/not is
 * the filter's. The sort is not kept (d06 names the filter and grid/table),
 * and a view counts only once the viewer chose one.
 */
export function payloadOf(state) {
	const savable = savableViewState(state || {}) || {};
	const filter = typeof savable.query === 'string' && savable.query.trim() ? savable.query.trim() : null;
	return { filter, view: VIEWS.includes(savable.mode) ? savable.mode : null };
}

/** The filter part of `payloadOf` for a query text alone ('' → null). */
export const filterToSave = query => payloadOf({ query }).filter;

/** What a list's saved state seeds the open list with: `{ query, mode }`, or null. */
function seedOf(viewerState) {
	if (!viewerState) return null;
	const seed = {};
	if (viewerState.filter) seed.query = viewerState.filter;
	if (VIEWS.includes(viewerState.view)) seed.mode = viewerState.view;
	return Object.keys(seed).length ? seed : null;
}

/**
 * The key a list's state is kept under: the card that DEFINES the list and the
 * list's id, as the server keys it. A list id alone is not enough — a clone
 * path copies list ids onto another card (KPR-735), and the section stays
 * mounted while the pane moves from an item of one card to an item of another.
 */
export const listStateKey = list => (list && list.id ? `${list.cardId || ''}:${list.id}` : '');

/**
 * A viewer's own filter and grid/table choice on each related list, saved to
 * their account (KPR-722) so it follows them to another device.
 *
 *   const account = useRelatedListAccountState({ itemId, lists });
 *   account.seedFor(list)          // { query?, mode? } from the account, or null
 *   account.save(list, state)      // the person changed it: saved, debounced
 *   account.reset(list)            // Reset: forgotten on the account
 *
 * `list` is an entry of `lists` ({ id, cardId, … }); everything is keyed by
 * `listStateKey(list)`.
 * - `lists` are `useRelatedLists`' answer: each carries the account's
 *   `viewerState` for that list (the server keys it on the card that defines
 *   the list and its id; what no longer applies it has already left out).
 * - `save` keeps only what the account holds: the filter WITHOUT the search
 *   term, and the grid/table choice only once the viewer made one. The sort
 *   is not saved (the epic's d06 names the filter and grid/table). A run of
 *   changes to one list is one request; it is sent at once when the section
 *   goes away, so nothing typed is lost.
 * - The writes for one list go out one after another, in the order they were
 *   made: a Reset pressed while a save is on the wire is sent after it, so it
 *   can never be overtaken and undone.
 * - Saving is a convenience: a failure is logged and nothing else — the list
 *   keeps the viewer's choices on screen, with no error drawn.
 */
export default function useRelatedListAccountState({ itemId, lists }) {
	const seeds = useMemo(() => {
		const map = new Map();
		(lists || []).forEach((list) => {
			const seed = seedOf(list.viewerState);
			if (seed) map.set(listStateKey(list), seed);
		});
		return map;
	}, [lists]);
	const seedFor = useCallback(list => seeds.get(listStateKey(list)) || null, [seeds]);

	const itemRef = useRef(itemId);
	itemRef.current = itemId;
	// key -> { timer, itemId, listId, payload }: a save waiting out its pause.
	const pending = useRef(new Map());
	// key -> the promise of that list's last write: the next one waits for it.
	const chains = useRef(new Map());
	// key -> what the account holds as far as this section knows (the seed,
	// then each save or reset sent): a change that alters nothing savable —
	// a search typed or cleared, a sort — sends nothing.
	const known = useRef(new Map());
	const knownOf = useCallback((list) => {
		const key = listStateKey(list);
		if (known.current.has(key)) return known.current.get(key);
		const viewerState = (lists || []).find(entry => listStateKey(entry) === key)?.viewerState;
		return JSON.stringify({ filter: viewerState?.filter || null, view: VIEWS.includes(viewerState?.view) ? viewerState.view : null });
	}, [lists]);

	const enqueue = useCallback((key, write) => {
		// Nothing of this list's on the wire: send now. Otherwise after it.
		const previous = chains.current.get(key);
		const started = previous ? previous.then(write) : new Promise((resolve) => { resolve(write()); });
		const next = started.catch((error) => {
			console.warn('Could not save this list\'s view to the account', error?.message || error);
		});
		chains.current.set(key, next);
		next.then(() => { if (chains.current.get(key) === next) chains.current.delete(key); });
	}, []);

	const send = useCallback((key) => {
		const entry = pending.current.get(key);
		if (!entry) return;
		clearTimeout(entry.timer);
		pending.current.delete(key);
		if (!entry.itemId) return;
		known.current.set(key, JSON.stringify(entry.payload));
		enqueue(key, () => saveRelatedListState(entry.itemId, entry.listId, entry.payload));
	}, [enqueue]);

	const save = useCallback((list, state) => {
		const key = listStateKey(list);
		if (!key) return;
		const payload = payloadOf(state);
		const previous = pending.current.get(key);
		if (previous) clearTimeout(previous.timer);
		if (JSON.stringify(payload) === knownOf(list)) {
			// Back to what the account holds (or never away from it): nothing to send.
			pending.current.delete(key);
			return;
		}
		// The item the change was made on: the server checks the list is one
		// its card shows, so a save that fires after the pane moved on still
		// lands on the list it belongs to.
		const timer = setTimeout(() => send(key), SAVE_DELAY_MS);
		pending.current.set(key, { timer, itemId: itemRef.current, listId: list.id, payload });
	}, [send, knownOf]);

	const reset = useCallback((list) => {
		const key = listStateKey(list);
		if (!key) return;
		const previous = pending.current.get(key);
		if (previous) {
			clearTimeout(previous.timer);
			pending.current.delete(key);
		}
		const id = itemRef.current;
		if (!id) return;
		known.current.set(key, JSON.stringify({ filter: null, view: null }));
		enqueue(key, () => resetRelatedListState(id, list.id));
	}, [enqueue]);

	// The section going away (the pane closed, the page left) sends what is
	// still waiting rather than dropping it.
	useEffect(() => {
		const map = pending.current;
		return () => { [...map.keys()].forEach(send); };
	}, [send]);

	return { seedFor, save, reset };
}
